/**
 * Email Invite Module
 *
 * Opens the user's default email client with a pre-filled invite message
 * for joining a MyPodOps Studio recording session.
 */

export interface InviteDetails {
  sessionTitle: string;
  hostName: string;
  inviteUrl: string;
  scheduledTime?: Date;
  additionalNotes?: string;
}

/**
 * Generate the email subject line
 */
function generateSubject(details: InviteDetails): string {
  return `You're invited to join "${details.sessionTitle}" on MyPodOps Studio`;
}

/**
 * Generate the email body with invite details
 */
function generateBody(details: InviteDetails): string {
  const lines: string[] = [];

  lines.push(`Hi there,`);
  lines.push(``);
  lines.push(`${details.hostName} has invited you to join a podcast recording session on MyPodOps Studio.`);
  lines.push(``);
  lines.push(`Session: ${details.sessionTitle}`);

  if (details.scheduledTime) {
    const formattedDate = details.scheduledTime.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const formattedTime = details.scheduledTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    lines.push(`When: ${formattedDate} at ${formattedTime}`);
  }

  lines.push(``);
  lines.push(`Click the link below to join:`);
  lines.push(details.inviteUrl);
  lines.push(``);

  if (details.additionalNotes) {
    lines.push(`Notes from ${details.hostName}:`);
    lines.push(details.additionalNotes);
    lines.push(``);
  }

  lines.push(`---`);
  lines.push(`Before joining, please:`);
  lines.push(`• Use a Chrome or Edge browser for best experience`);
  lines.push(`• Test your microphone and camera`);
  lines.push(`• Find a quiet location with good lighting`);
  lines.push(`• Use headphones to prevent echo`);
  lines.push(``);
  lines.push(`See you there!`);
  lines.push(``);
  lines.push(`---`);
  lines.push(`Powered by MyPodOps Studio`);
  lines.push(`Professional Podcast Recording`);
  lines.push(`https://www.mypodops.com`);

  return lines.join('\n');
}

/**
 * Build a mailto: URL with the invite details
 */
export function buildMailtoUrl(details: InviteDetails, recipientEmail?: string): string {
  const subject = encodeURIComponent(generateSubject(details));
  const body = encodeURIComponent(generateBody(details));

  let mailto = 'mailto:';
  if (recipientEmail) {
    mailto += encodeURIComponent(recipientEmail);
  }
  mailto += `?subject=${subject}&body=${body}`;

  return mailto;
}

/**
 * Open the default email client with invite details
 *
 * @param details - The invite details to include in the email
 * @param recipientEmail - Optional recipient email address
 * @returns true if the email client was opened successfully
 */
export function openEmailInvite(details: InviteDetails, recipientEmail?: string): boolean {
  try {
    const mailtoUrl = buildMailtoUrl(details, recipientEmail);

    // Open in a new window/tab - this triggers the default email client
    const newWindow = window.open(mailtoUrl, '_blank');

    // Some browsers block window.open, so try location.href as fallback
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      window.location.href = mailtoUrl;
    }

    console.log('[EmailInvite] Opened email client for invite:', details.sessionTitle);
    return true;
  } catch (error) {
    console.error('[EmailInvite] Failed to open email client:', error);
    return false;
  }
}

/**
 * Generate an invite URL from session/meeting details
 */
export function generateInviteUrl(
  sessionId: string,
  meetingId: string,
  token: string,
  baseUrl?: string
): string {
  const base = baseUrl || window.location.origin;
  return `${base}/studio-v2/join?session=${sessionId}&meeting=${meetingId}&token=${token}`;
}

/**
 * Copy invite link to clipboard
 *
 * @returns Promise that resolves to true if successful
 */
export async function copyInviteToClipboard(inviteUrl: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(inviteUrl);
    console.log('[EmailInvite] Invite link copied to clipboard');
    return true;
  } catch (error) {
    console.error('[EmailInvite] Failed to copy to clipboard:', error);

    // Fallback for older browsers
    try {
      const textArea = document.createElement('textarea');
      textArea.value = inviteUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    } catch (fallbackError) {
      console.error('[EmailInvite] Fallback copy failed:', fallbackError);
      return false;
    }
  }
}

/**
 * Quick helper to send an invite with minimal parameters
 */
export function sendInvite(
  sessionTitle: string,
  hostName: string,
  sessionId: string,
  meetingId: string,
  token: string,
  recipientEmail?: string
): boolean {
  const inviteUrl = generateInviteUrl(sessionId, meetingId, token);

  return openEmailInvite({
    sessionTitle,
    hostName,
    inviteUrl
  }, recipientEmail);
}
